import { MaintenanceBoundary } from '@/maintenance/MaintenanceScreen';
import { useQueryClient } from '@tanstack/react-query';
import { useMaintenance } from '@/maintenance/store';
import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { UpdateBanner } from '@/pwa/UpdateBanner';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { RouteErrorBoundary } from '@/app/RouteErrorBoundary';
import { DesktopLayout } from './DesktopLayout';
import { MobileLayout } from './MobileLayout';
import { ConnectivityIndicator } from '@/design-system/components/ConnectivityIndicator/ConnectivityIndicator';
import { LoadingSpinner } from '@/design-system/components/LoadingSpinner/LoadingSpinner';
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge';
import { GuideProvider } from '@/features/guide/GuideProvider';
import { useScrapLookupPrefetch } from '@/features/scraptrack/hooks/useScrapTrack';
import { GuidePanel } from '@/features/guide/GuidePanel';
import { AssistantButton } from './AssistantButton';
import { CornerStack } from './CornerStack';
import { PreviewBanner } from './PreviewBanner';

// Fallback shown in the content area while a lazily-loaded page chunk downloads.
// The layout shell (nav / header) stays mounted around it.
function PageFallback() {
  return (
    <div className="flex items-center justify-center py-24">
      <LoadingSpinner size="lg" />
    </div>
  );
}

export function RootLayout() {
  const maintenance = useMaintenance();
  const wasMaintenance = useRef(maintenance);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (wasMaintenance.current && !maintenance) void queryClient.invalidateQueries();
    wasMaintenance.current = maintenance;
  }, [maintenance, queryClient]);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const location = useLocation();
  // Warm the scrap ticket sheet's six lookups while online, so offline ticket
  // capture in the yard has working pickers (self-gates on scraptrack.view).
  useScrapLookupPrefetch();
  // Where the closed Guide launcher sits in the bottom-right column.
  const [guideSlot, setGuideSlot] = useState<HTMLDivElement | null>(null);

  // Error boundary wraps ONLY the routed content: a throwing page shows the
  // plain-language panel while the shell/nav stay alive; navigating resets it.
  const content = (
    <MaintenanceBoundary>
      <RouteErrorBoundary resetKey={location.pathname}>
        <UpdateBanner />
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </RouteErrorBoundary>
    </MaintenanceBoundary>
  );

  return (
    <div className="min-h-screen bg-graphite-950">
      {/* No shared NADC bar above the shells since 2026-10-09 (Michael, to
          save screen space): index.html stopped loading nadc1.net's
          navbar.js, so nothing sets body.has-nadc-navbar. The bar's launcher
          and user menu are the NADC apps button in both headers
          (NadcAppsLauncher). */}
      {/* preview.nadc1.net only: the band that says so, with Exit (2026-09-28). */}
      <PreviewBanner />
      {/* Offline: told to screen readers only, so it never lies over the
          header (2026-10-09); the headers' sync pill is what shows it. */}
      <ConnectivityIndicator />
      {/* DemoOps Guide: mounted once here so pages only ever register with it.
          Renders nothing until a page does. */}
      <GuideProvider>
        {isDesktop ? (
          <DesktopLayout>{content}</DesktopLayout>
        ) : (
          <MobileLayout>{content}</MobileLayout>
        )}
        <GuidePanel launcherSlot={guideSlot} />
        {/* The bottom-right corner (2026-10-28): one column, so these never
            land on top of each other — Read-only, then the Guide launcher,
            then NADC.AI (2026-09-25: a persistent button at the bottom of
            every screen instead of a menu row). */}
        <CornerStack>
          <ReadOnlyBadge />
          <div ref={setGuideSlot} className="contents" />
          <AssistantButton />
        </CornerStack>
      </GuideProvider>
    </div>
  );
}
